import { VStack } from "@/components/Layout";
import { Command, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/cn";
import InviteComboboxSelectedPersonOption from "@/routes/_staff/settings/persons/_list/_account/-components/TabFilterBar/InviteComboboxSelectedPersonOption";
import InviteComboboxSelectedPersonOptionChip from "@/routes/_staff/settings/persons/_list/_account/-components/TabFilterBar/InviteComboboxSelectedPersonOptionChip";
import type { SelectedPersonOption } from "@/routes/_staff/settings/persons/_list/_account/-types";
import { useActorRef } from "@xstate/react";
import { CommandInput } from "cmdk";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPersonSelectMachine } from "../-xstate/person-select-machine";
import type { Attribute, FieldRenderInputProps } from "../../types";
import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";
import { ReadOnlyField } from "./ReadOnlyField";

/**
 * 人を検索するフィールドコンポーネント
 * 人を検索するフィールドの入力を提供します
 */
export const PersonSelectField = memo(
  ({
    fieldId,
    field,
    disabled,
    readOnly,
    layout,
    className,
    "data-testid": testId,
  }: BaseFieldProps) => {
    const renderInput = useCallback(
      ({
        value: inputValue,
        onChange: handleChange,
        placeholder,
        disabled: isDisabled,
      }: FieldRenderInputProps) => {
        const machine = useMemo(() => createPersonSelectMachine(), []);
        const stateMachineService = useActorRef(machine, {
          input: {
            selectedPersonId: inputValue as string,
          },
        });

        const state = useSyncExternalStore(
          (callback) => {
            const subscription = stateMachineService.subscribe(callback);
            return () => subscription.unsubscribe();
          },
          () => stateMachineService.getSnapshot(),
        );

        const isSyncedRef = useRef(false);
        const {
          selectedPersonId: currentSelectedPersonId,
          selectedPersonOption: currentSelectedPersonOption,
        } = state.context;

        useEffect(() => {
          // INIT を初回のみ実行
          if (!isSyncedRef.current) {
            stateMachineService.send({ type: "INIT" });
            isSyncedRef.current = true;
            return;
          }

          if (
            inputValue === undefined &&
            currentSelectedPersonOption !== null
          ) {
            stateMachineService.send({
              type: "REMOVE_PERSON",
              personId: currentSelectedPersonId,
            });
            handleChange(undefined);
          } else if (
            inputValue !== undefined &&
            (currentSelectedPersonOption === null ||
              currentSelectedPersonId !== inputValue)
          ) {
            stateMachineService.send({
              type: "GET_PERSON",
              personId: inputValue as string,
            });
          }
        }, [
          inputValue,
          stateMachineService,
          handleChange,
          currentSelectedPersonId,
          currentSelectedPersonOption,
        ]);

        if (!field) {
          return <div></div>;
        }

        const context = state.context;
        const selectedPersonOption = context.selectedPersonOption;
        const searchText = context.searchText;
        const searchResults = context.searchResults;

        const handlePersonSelect = (
          personId: string,
          option?: SelectedPersonOption,
        ) => {
          if (option) {
            stateMachineService.send({
              type: "SELECT_PERSON",
              personId,
              personOption: option,
            });
            handleChange(option.id);
          }
        };

        const handlePersonRemove = (personId: string) => {
          stateMachineService.send({ type: "REMOVE_PERSON", personId });
          handleChange(undefined);
        };

        const handleSearchChange = (text: string) => {
          stateMachineService.send({ type: "SEARCH_PERSON", text });
        };

        return (
          <VStack className="gap-2">
            <PersonSelection
              options={searchText ? searchResults : []}
              value={selectedPersonOption?.id ?? null}
              selectedPersonOption={selectedPersonOption}
              searchText={searchText}
              onSearchChange={handleSearchChange}
              onValueChange={handlePersonSelect}
              onRemovePerson={handlePersonRemove}
              disabled={isDisabled}
              readonly={readOnly || field.readonly}
              field={field}
              placeholder={placeholder}
            />
          </VStack>
        );
      },
      [field, readOnly],
    );
    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        disabled={disabled}
        readOnly={false} //内部で切り替えるので
        layout={layout}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

function PersonSelection(props: {
  options: SelectedPersonOption[];
  value: string | null;
  selectedPersonOption: SelectedPersonOption | null;
  searchText: string;
  onSearchChange?: (text: string) => void;
  onValueChange: (value: string, option?: SelectedPersonOption) => void;
  onRemovePerson?: (personId: string) => void;
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: string;
  field: Attribute;
}) {
  // NOTE: 検索テキストの選択後にクリアするために保持
  const [searchText, setSearchText] = useState("");

  const handleSearchChange = useCallback(
    (text: string) => {
      props.onSearchChange?.(text);
      setSearchText(text);
    },
    [props.onSearchChange],
  );

  const handleOnSelect = useCallback(
    (value: string) => {
      const option = props.options.find((opt) => opt.value === value);
      if (option && !option.disabled) {
        props.onValueChange(value, option);
        setSearchText("");
      }
    },
    [props.onValueChange, props.options],
  );

  const handleRemoveSelectedOption = useCallback(
    (value: string) => {
      props.onRemovePerson?.(value);
    },
    [props.onRemovePerson],
  );
  return (
    <>
      {props.readonly ? (
        <ReadOnlyField
          value={props.selectedPersonOption?.label}
          field={props.field}
        />
      ) : (
        <Command shouldFilter={false}>
          <div className="flex min-h-[80px] gap-2 rounded-md border border-b border-neutral-muted-border px-3 py-2 shadow-sm">
            <div className="flex flex-1 flex-wrap items-start gap-1 py-2">
              {props.selectedPersonOption ? (
                <InviteComboboxSelectedPersonOptionChip
                  key={props.selectedPersonOption.id}
                  option={props.selectedPersonOption}
                  onSelect={handleRemoveSelectedOption}
                  disabled={props.disabled}
                />
              ) : (
                <CommandInput
                  className="h-6 w-full text-xs outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={props.placeholder}
                  value={searchText}
                  onValueChange={handleSearchChange}
                  disabled={props.disabled}
                />
              )}
            </div>
          </div>
          {props.options.length ? (
            <CommandList
              className={cn(
                "gap-1 rounded-md border border-neutral-muted-border bg-neutral-fill p-1 shadow-sm",
              )}
            >
              {props.options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.value}
                  onSelect={
                    option.disabled
                      ? undefined
                      : () => handleOnSelect(option.value)
                  }
                  className={cn(
                    "cursor-pointer gap-1",
                    option.disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <InviteComboboxSelectedPersonOption person={option} />
                </CommandItem>
              ))}
            </CommandList>
          ) : (
            <></>
          )}
        </Command>
      )}
    </>
  );
}

PersonSelectField.displayName = "PersonSelectField";
